import { type AxiosRequestConfig, HttpStatusCode } from 'axios';
import { client } from '@/api/http';
import { AppError } from '@/stores/appErrorStore';
import { errorDetail } from '@/api/errorDetail';

/** The size of a two-dimensional (2d) image. */
export interface ImageSize {
    /**
     * The height in pixels. This should be a whole number (integer).
     */
    height: number;

    /**
     * The width in pixels. This should be a whole number (integer).
     */
    width: number;
}

export type ApiCaseCatStack = {
    image_url: string;

    /**
     * The number of catstack slices (sprites) in the image.
     */
    count: number;

    /**
     * The overall size of the image in pixels. This should be a multiple of the slice (sprite)
     * size.
     *
     * Note: the image size / slice should be a whole number.
     */
    image: ImageSize;

    /**
     * The size of each slice (sprite) within the image. Each slice is assumed to be of the
     * same size.
     *
     * e.g. 512 pixels high, by 512 pixels wide.
     */
    slice: ImageSize;

    /**
     * The size of the slice (sprite) in millimetres (mm).
     */
    world: ImageSize;
};

export type XYZ = { x: number; y: number; z: number };

export type ApiBoundingBox = {
    max: XYZ;
    min: XYZ;
};

export type ApiCaseCatStacks = {
    bounding_box: ApiBoundingBox;
    axial: ApiCaseCatStack;
    coronal: ApiCaseCatStack;
    sagittal: ApiCaseCatStack;
};

export async function fetchCatStacks(
    caseId: number,
    config?: AxiosRequestConfig,
): Promise<ApiCaseCatStacks | null> {
    const response = await client.get<ApiCaseCatStacks>(`/cases/${caseId}/cat-stacks`, config);
    const { status, data } = response;
    if (status === HttpStatusCode.Ok) {
        return data;
    } else if (status === HttpStatusCode.Forbidden) {
        throw new AppError(
            'Case not found' + errorDetail(data),
            'This case does not exist or you do not have permission to view it.',
        );
    } else if (status === HttpStatusCode.NotFound) {
        return null;
    } else {
        throw Error(`Failed to load cat-stack`);
    }
}
